import React, { FunctionComponent, useState, useCallback } from 'react';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { ProductTestimonialSlideModel } from '../models/productSeriesDetailTestimonial.model';
import { getThumbnailProps } from '../../../utils/youtube.utils';
import MediaElement from './mediaElement';
import PrimaryButton from './primaryButton';
import SecondaryButton from './secondaryButton';
import VideoPopup from './videoPopup';

const CarouselSlide: FunctionComponent<{
  model: ProductTestimonialSlideModel;
  isEE?: boolean;
  iconImage: string;
  isDesktop: boolean;
}> = ({ model, isEE = false, iconImage, isDesktop }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const isMediaShown = model.asset?.rendition_desktop_size1000?.url || model.asset?.rendition_mobile_size500?.url;
  const useVideo = (model.asset?.role === 'video' || model.asset?.role === 'extvideo') && !!model.asset.full;
  const { thumbnailImgSrc } = model.asset?.role === 'extvideo' ? getThumbnailProps(model?.asset?.full || '') : { thumbnailImgSrc: '' };
  const showPrimaryButton = isEE || (model.buttonPrimary?.value?.title && model.buttonPrimary?.value?.href);
  const showSecondaryButton =
    (model.cardStyles.value === 'Wide' || !isDesktop) &&
    (isEE || (model.buttonSecondary?.value?.title && model.buttonSecondary?.value?.href));
  const closeOverlayModal = (): void => setShowOverlay(false);
  const showOverlayVideo = (): void => {
    if (!model?.asset?.full) return;
    setShowOverlay(true);
  };

  const getOverlayProps = useCallback(() => {
    const thumbnailImg = { value: { src: thumbnailImgSrc } };
    return [
      {
        desktopImage: thumbnailImgSrc ? thumbnailImg : model?.image,
        mobileImage: thumbnailImgSrc ? thumbnailImg : model?.mobileImage,
        desktopAlt: model?.imageAlt,
        mobileAlt: model?.imageAlt,
        isVideo: useVideo,
        videoUrl: model?.asset?.full,
      },
    ];
  }, [model]); // eslint-disable-line

  return (
    <>
      <div className={`testimonial-card testimonial-card--${model.cardStyles.value === 'Wide' ? 'large' : 'small'}`}>
        <div className="testimonial-card__with-header-image slide-card slide-card--strech">
          <MediaElement
            isMediaShown={isMediaShown}
            useVideo={useVideo}
            desktopRendition={model.asset?.rendition_desktop_size1000}
            mobileRendition={model.asset?.rendition_mobile_size500}
            altText={model.imageAlt}
            assetRole={model.asset?.role}
            showOverlayVideo={showOverlayVideo}
            thumbnailImgSrc={thumbnailImgSrc}
          />
          <div className="testimonial-card__content">
            <div className="testimonial-card__body">
              <div className="testimonial-card__title">
                <RichText
                  tag="h3"
                  field={model.title}
                  className="slide-card__text slide-card__text--inline slide-card__text--overflow-unset"
                />
              </div>
              {model.description && <RichText tag="div" field={model.description} className="testimonial-card__description rich-text" />}
            </div>
            {(showPrimaryButton || showSecondaryButton) && (
              <div className="testimonial-card__button-wrapper">
                <PrimaryButton buttonPrimary={model?.buttonPrimary} showPrimaryButton={showPrimaryButton} isDesktop={isDesktop} />
                <SecondaryButton
                  showSecondaryButton={showSecondaryButton}
                  buttonSecondary={model.buttonSecondary}
                  isDesktop={isDesktop}
                  iconImage={iconImage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <VideoPopup useVideo={useVideo} showOverlay={showOverlay} closeOverlayModal={closeOverlayModal} getOverlayProps={getOverlayProps} />
    </>
  );
};

export default CarouselSlide;
